import React, { useState, FormEvent, useMemo } from "react";
import { Graph3DNode, LocalConnection } from "../../types";
import LocalLawList from "./LocalLawList";
import { getNodeExplanation } from "../../api";

interface SearchFilterControlsProps {
  onSearch: (query: string) => void;
  onNodeSelect: (node: Graph3DNode) => void;
  nodes: Graph3DNode[];
  selectedFilters: string[];
  setSelectedFilters: (filters: string[]) => void;
  LAW_TYPES: { name: string; mstCode: string; color: string }[];
  currentState: string;
  setCurrentState: (state: string) => void;
  selectedNode: Graph3DNode | null;
  localConnections: LocalConnection[];
}
const SearchBar = ({ handleSearch, searchQuery, setSearchQuery }: any) => {
  return (
    <>
      <form onSubmit={handleSearch} style={{ display: "flex", gap: "8px" }}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search nodes..."
          style={{
            flex: 1,
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ddd",
          }}
        />
        <button
          type="submit"
          style={{
            padding: "8px 16px",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Search
        </button>
      </form>
    </>
  );
};
const ToggleButton = ({ currentState, setCurrentState }: any) => {
  return (
    <>
      <div style={{ display: "flex", gap: "8px" }}>
        <button
          onClick={() => setCurrentState("법규체계")}
          style={{
            padding: "8px 16px",
            backgroundColor:
              currentState === "법규체계" ? "#007bff" : "#f0f0f0",
            color: currentState === "법규체계" ? "white" : "#333",
            border: "1px solid #ddd",
            borderRadius: "4px",
            cursor: "pointer",
            transition: "all 0.2s ease",
            fontWeight: "500",
            boxShadow:
              currentState === "법규체계"
                ? "0 2px 4px rgba(0,0,0,0.1)"
                : "none",
          }}
        >
          법규체계
        </button>
        <button
          onClick={() => setCurrentState("지자체조례")}
          style={{
            padding: "8px 16px",
            backgroundColor:
              currentState === "지자체조례" ? "#007bff" : "#f0f0f0",
            color: currentState === "지자체조례" ? "white" : "#333",
            border: "1px solid #ddd",
            borderRadius: "4px",
            cursor: "pointer",
            transition: "all 0.2s ease",
            fontWeight: "500",
            boxShadow:
              currentState === "지자체조례"
                ? "0 2px 4px rgba(0,0,0,0.1)"
                : "none",
          }}
        >
          지자체조례
        </button>
      </div>
    </>
  );
};
const SearchFilterControls: React.FC<SearchFilterControlsProps> = ({
  onSearch,
  onNodeSelect,
  nodes,
  selectedFilters,
  setSelectedFilters,
  LAW_TYPES,
  currentState,
  setCurrentState,
  selectedNode,
  localConnections,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const filteredNodes = useMemo(() => {
    if (!searchQuery) return [];
    return nodes.filter((node) => {
      const matchesSearch =
        node.law_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        node.number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        node.short_content?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesFilters =
        selectedFilters.length === 0 ||
        selectedFilters.includes(node.mst_code || "");

      return matchesSearch && matchesFilters;
    });
  }, [nodes, searchQuery, selectedFilters]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    onSearch(searchQuery);
  };

  const handleFilterChange = (mstCode: string) => {
    const newFilters = selectedFilters.includes(mstCode)
      ? selectedFilters.filter((f) => f !== mstCode)
      : [...selectedFilters, mstCode];

    setSelectedFilters(newFilters);
  };

  const handleNodeClick = (node: Graph3DNode) => {
    onNodeSelect(node);
    setSearchQuery(""); // Clear search after selection
  };
  const onClickConnection = async (connection: LocalConnection) => {};

  return (
    <div className="fixed left-0 bottom-0 w-80 max-w-full max-h-[60vh] bg-white rounded-t-lg shadow-lg flex flex-col gap-4 p-4 overflow-y-auto z-40 border-t border-r">
      <ToggleButton
        currentState={currentState}
        setCurrentState={setCurrentState}
      />
      {currentState === "법규체계" ? (
        <SearchBar
          handleSearch={handleSearch}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
      ) : (
        selectedNode &&
        localConnections.length > 0 && (
          <div className="relative w-full">
            <LocalLawList
              selectedNode={selectedNode}
              localConnections={localConnections}
              onClickConnection={onClickConnection}
            />
          </div>
        )
      )}
      {currentState === "법규체계" &&
        searchQuery &&
        filteredNodes.length > 0 && (
          <div className="flex flex-col gap-2 max-h-52 overflow-y-auto p-2 bg-black/5 rounded">
            {filteredNodes.map((node) => (
              <div
                key={node.id}
                onClick={() => handleNodeClick(node)}
                className="p-2 cursor-pointer rounded bg-white border border-gray-200 transition-colors hover:bg-gray-100"
              >
                <div className="font-bold">
                  {node.law_name} {node.number}
                </div>
                {node.short_content && (
                  <div className="text-sm text-gray-600 mt-1">
                    {node.short_content.slice(0, 100)}...
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      <div className="flex gap-4 mt-2">
        {LAW_TYPES.map((lawType) => (
          <label key={lawType.mstCode} className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={selectedFilters.includes(lawType.mstCode)}
              onChange={() => handleFilterChange(lawType.mstCode)}
            />
            <div
              className="px-1 rounded text-xs font-mono"
              style={{ backgroundColor: lawType.color }}
            >
              {lawType.name}
            </div>
          </label>
        ))}
      </div>
    </div>
  );
};

export default SearchFilterControls;
